import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { LIST_ALL_POINTS } from "../graphql/queries/list-all-points";
import { ApolloError } from "apollo-boost";
import { ICluster } from "./useCluster";
import { List_All_PointsQuery } from "../generated/graphql";
import { LoadingContext } from "../contexts/LoadingContent";

interface IReturn {
  refetch: () => void;
  loading: boolean;
  error?: ApolloError;
  points: IPoints;
  getPoints: () => void;
}

export interface IPoints {
  storyPoints: ICluster[];
  eventPoints: ICluster[];
  sitePoints: ICluster[];
}

export const usePOI: () => IReturn = () => {
  const [skip, setSkip] = React.useState(false);
  const [loadedState, setLoading] = React.useContext(LoadingContext);

  const { loading, error, data, refetch } = useQuery<List_All_PointsQuery>(
    LIST_ALL_POINTS,
    {
      skip,
    }
  );
  const [points, setPoints] = React.useState<IPoints>({
    storyPoints: [],
    eventPoints: [],
    sitePoints: [],
  });

  const getPoints = React.useCallback(() => {
    setSkip(false);
    refetch().catch((error) => {
      setLoading({
        ...loadedState,
        error: "Failed to fetch points of interest",
        pinsLoaded: true,
      });
    });
  }, [loadedState, refetch, setLoading]);

  const getClusters = React.useCallback(() => {
    let storyPoints: ICluster[] = [];
    let sitePoints: ICluster[] = [];
    let eventPoints: ICluster[] = [];

    data?.nodeQuery?.entities?.forEach((entity) => {
      if (entity?.__typename === "NodeHeritageSite") {
        if (entity.x && entity.y && entity.entityId) {
          sitePoints.push({
            x: entity.x,
            y: entity.y,
            qty: 1,
            type: "point",
            id: entity.entityId,
            data: {
              title: entity.title,
              id: entity.entityId,
            },
          });
        }
      }
      if (entity?.__typename === "NodeEvent") {
        if (
          entity.x &&
          entity.y &&
          entity.entityId &&
          !entity.fieldEventHeritageSite
        ) {
          eventPoints.push({
            x: entity.x,
            y: entity.y,
            qty: 1,
            type: "point",
            id: entity.entityId,
            data: {
              title: entity.title,
              id: entity.entityId,
            },
          });
        }
      }
      if (entity?.__typename === "NodeSayings") {
        if (entity.x && entity.y && entity.entityId) {
          storyPoints.push({
            x: entity.x,
            y: entity.y,
            qty: 1,
            type: "point",
            id: entity.entityId,
            data: {
              title: entity.title,
              id: entity.entityId,
            },
          });
        }
      }
    });
    return { storyPoints, sitePoints, eventPoints };
  }, [data]);

  React.useEffect(() => {
    if (data) {
      const { storyPoints, sitePoints, eventPoints } = getClusters();
      setLoading({ ...loadedState, error: null, pinsLoaded: true });
      setPoints({
        storyPoints,
        sitePoints,
        eventPoints,
      });
      return () => setSkip(true);
    }
  }, [data, getClusters, loadedState, setLoading]);

  return { refetch, error, loading, points, getPoints };
};
